import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/axios';
import Breadcrumb from '../../components/ui/Breadcrumb';

const ClientEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', reason: '',
    contact_name: '', contact_phone: '', contact_email: '', contact_role: '',
    assigned_user_id: '', status: 'active',
  });

  useEffect(() => {
    Promise.all([
      api.get(`/clients/${id}`),
      api.get('/users'),
    ]).then(([clientRes, usersRes]) => {
      const c = clientRes.data;
      setForm({
        name: c.name || '', phone: c.phone || '', email: c.email || '',
        address: c.address || '', reason: c.reason || '',
        contact_name: c.contact_name || '', contact_phone: c.contact_phone || '',
        contact_email: c.contact_email || '', contact_role: c.contact_role || '',
        assigned_user_id: c.assigned_user_id || '', status: c.status || 'active',
      });
      setUsers(usersRes.data);
    }).finally(() => setFetching(false));
  }, [id]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/clients/${id}`, form);
      navigate(`/clients/${id}`);
    } catch (error) {
      if (error.response?.status === 422) setErrors(error.response.data.errors);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return (
    <div className="text-center py-5">
      <div className="spinner-border text-primary"></div>
    </div>
  );

  return (
    <div className="container-fluid">
      <Breadcrumb items={[
        { label: 'Clientes', to: '/clients' },
        { label: form.name || 'Cliente', to: `/clients/${id}` },
        { label: 'Editar' },
      ]} />
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Editar Cliente</h4>
        <Link to={`/clients/${id}`} className="btn btn-outline-secondary btn-sm">
          <i className="ti-arrow-left mr-1"></i> Regresar
        </Link>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Datos del cliente */}
        <div className="card mb-4">
          <div className="card-header"><h6 className="mb-0">Datos del Cliente</h6></div>
          <div className="card-body">
            <div className="row">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Nombre <span className="text-danger">*</span></label>
                  <input type="text" name="name"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={form.name} onChange={handleChange} required />
                  {errors.name && <div className="invalid-feedback">{errors.name[0]}</div>}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Teléfono</label>
                  <input type="text" name="phone" className="form-control"
                    value={form.phone} onChange={handleChange} />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Correo electrónico</label>
                  <input type="email" name="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    value={form.email} onChange={handleChange} />
                  {errors.email && <div className="invalid-feedback">{errors.email[0]}</div>}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Estado</label>
                  <select name="status" className="form-control" value={form.status} onChange={handleChange}>
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                    <option value="completed">Completado</option>
                  </select>
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label>Dirección</label>
                  <input type="text" name="address" className="form-control"
                    value={form.address} onChange={handleChange} />
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label>Motivo <span className="text-danger">*</span></label>
                  <textarea name="reason" rows="3"
                    className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                    value={form.reason} onChange={handleChange} required></textarea>
                  {errors.reason && <div className="invalid-feedback">{errors.reason[0]}</div>}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Responsable <span className="text-danger">*</span></label>
                  <select name="assigned_user_id"
                    className={`form-control ${errors.assigned_user_id ? 'is-invalid' : ''}`}
                    value={form.assigned_user_id} onChange={handleChange} required>
                    <option value="">Seleccionar...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                  {errors.assigned_user_id && <div className="invalid-feedback">{errors.assigned_user_id[0]}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Datos del contacto */}
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">
              Contacto <small className="text-muted ml-2">(Broker, gestor u otro intermediario — opcional)</small>
            </h6>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Nombre del contacto</label>
                  <input type="text" name="contact_name" className="form-control"
                    value={form.contact_name} onChange={handleChange} />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Rol / Cargo</label>
                  <input type="text" name="contact_role" className="form-control"
                    placeholder="ej. Broker, Gestor, Apoderado"
                    value={form.contact_role} onChange={handleChange} />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Teléfono del contacto</label>
                  <input type="text" name="contact_phone" className="form-control"
                    value={form.contact_phone} onChange={handleChange} />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-group">
                  <label>Correo del contacto</label>
                  <input type="email" name="contact_email" className="form-control"
                    value={form.contact_email} onChange={handleChange} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
          <Link to={`/clients/${id}`} className="btn btn-outline-secondary">Cancelar</Link>
        </div>
      </form>
    </div>
  );
};

export default ClientEdit;